import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  computeControlTotals,
  computeMainGroupPercents,
  formatSwissCurrency,
} from "../../services/costs/controlCalculations.js";
import { exportCostControlToExcel } from "../../services/costs/controlExcelExport.js";
import {
  getContractsByEstimate,
  saveContract,
  updateContract,
  deleteContract,
} from "../../services/azureCostEstimateContractsService.js";
import {
  getPaymentsByEstimate,
  savePayment,
  updatePayment,
  deletePayment,
} from "../../services/azureCostEstimatePaymentsService.js";
import { getAllPartners } from "../../services/azurePartnersService.js";
import CostControlDetailPanel from "./CostControlDetailPanel.jsx";
import SaveStatusIndicator from "./SaveStatusIndicator.jsx";
import ZahlungsanweisungModal from "./ZahlungsanweisungModal.jsx";

const VIEW_KEY = "costControlView";

const groupByCode = (list) => {
  const map = {};
  for (const item of list) {
    if (!map[item.code]) map[item.code] = [];
    map[item.code].push(item);
  }
  return map;
};

const CostControlEditor = ({
  estimateId,
  tree,
  expanded,
  toggleExpand,
  showInactive,
  setShowInactive,
  editControlField,
  activatePosition,
  deletePosition,
  addChild,
  saveBatch,
  isSaving,
  isDirty,
  lastSavedAt,
  lastSaveKind,
  readOnly = false,
  currentPhase = "",
  estimateName = "",
  linkedProject = null,
  importKvBase,
  kvBaseImported = false,
  projectEstimates = [],
  currentEstimateId = null,
  searchTerm = "",
  activeFilters = [],
  isFullscreen = false,
  toggleFullscreen,
  canManageMembers = false,
  onManageMembers,
  // US-KOST-022 (Zahlungsanweisung): Auftraggeberin-Fallback aus dem Dossier
  // und Name der angemeldeten Person fuer die Unterschrift.
  estimateClient = "",
  currentUserName = "",
  // US-KOST-023: Objektgliederung der aktuellen Phase (Sidebar), erscheint auf
  // allen Zahlungsanweisungen dieser Phase.
  objektgliederung = "",
}) => {
  // Ansicht (KOST-008): 'control' | 'kv'. Auswahl bleibt für die Session erhalten.
  const [view, setView] = useState(() => {
    try {
      return sessionStorage.getItem(VIEW_KEY) === "kv" ? "kv" : "control";
    } catch {
      return "control";
    }
  });
  const changeView = useCallback((v) => {
    setView(v);
    try {
      sessionStorage.setItem(VIEW_KEY, v);
    } catch {
      /* ignore */
    }
  }, []);

  const [contractsByCode, setContractsByCode] = useState({});
  const [paymentsByCode, setPaymentsByCode] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [editing, setEditing] = useState(null); // { code, field }
  const [localValue, setLocalValue] = useState("");
  const inputRef = useRef(null);

  const [openPanels, setOpenPanels] = useState(() => new Set());
  const [panelOpenWith, setPanelOpenWith] = useState({}); // { code: 'contract'|'payment' }
  const [contextMenu, setContextMenu] = useState(null);

  const [kvBaseBusy, setKvBaseBusy] = useState(false);
  const [kvModal, setKvModal] = useState(null); // { overwrite }
  // mode nur relevant bei Kostenkontroll-Quellen ("41 ..."): 'kv' | 'prognose'
  const [kvSource, setKvSource] = useState({ estimateId: "", phase: "", mode: "kv" });

  // Löschen einer Position mit Verträgen/Zahlungen (KOST-017):
  // { code, label, isAggregate, subtreeCodes, contracts, payments, mode: 'reassign'|'delete', targetCode, targetOptions }
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Zahlungsanweisung (US-KOST-022): { code, payment } | null —
  // payment gesetzt = bestehende Zahlungsanweisung bearbeiten (US-KOST-023).
  const [zaModal, setZaModal] = useState(null);
  // CRM-Firmen (projectPartners) fuer Unternehmer-Auswahl und Auftraggeberin.
  // Einmal pro Editor-Instanz geladen, nach CRM-Neuerfassung nachgeladen.
  const [partners, setPartners] = useState([]);
  const reloadPartners = useCallback(async () => {
    try {
      setPartners(await getAllPartners());
    } catch {
      /* Auswahl bleibt dann leer; Neuerfassung weiterhin moeglich */
    }
  }, []);
  useEffect(() => {
    reloadPartners();
  }, [reloadPartners]);

  // === Verträge/Zahlungen laden ===
  useEffect(() => {
    let cancelled = false;
    setDataLoading(true);
    setLoadError("");
    (async () => {
      try {
        const [contracts, payments] = await Promise.all([
          getContractsByEstimate(estimateId, currentPhase),
          getPaymentsByEstimate(estimateId, currentPhase),
        ]);
        if (cancelled) return;
        setContractsByCode(groupByCode(contracts));
        setPaymentsByCode(groupByCode(payments));
      } catch (err) {
        if (!cancelled) setLoadError(`Verträge/Zahlungen konnten nicht geladen werden: ${err?.message || "Unbekannter Fehler"}`);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId, currentPhase]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  // Kontextmenü bei Klick ausserhalb schliessen (KOST-012).
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [contextMenu]);

  // === Rollups ===
  const { totals, grand } = useMemo(
    () => computeControlTotals(tree, { contractsByCode, paymentsByCode }),
    [tree, contractsByCode, paymentsByCode]
  );
  const percents = useMemo(() => computeMainGroupPercents(tree, totals), [tree, totals]);
  // Alle Zahlungen der Phase ueber alle Positionen — fuer die dossierweite
  // Nummerierung der Zahlungsanweisungen (US-KOST-023).
  const allPayments = useMemo(() => Object.values(paymentsByCode).flat(), [paymentsByCode]);

  const treeHasData = useMemo(() => Object.values(tree).some((n) => n.active), [tree]);

  // === Suche/Filter (Sidebar-Suchfeld, gleiches Verhalten wie EstimateEditor) ===
  // Ergebnis: Set der Codes, die selbst matchen ODER auf dem Pfad zu einem Treffer
  // liegen (Vorfahren); null = keine Suche aktiv.
  const filteredCodes = useMemo(() => {
    const term = (searchTerm || "").trim();
    const hasFilters = (activeFilters || []).length > 0;
    if (!term && !hasFilters) return null;

    const matchOne = (node, t) => {
      const q = t.toLowerCase();
      return (
        node.code.toLowerCase().includes(q) ||
        (node.name && node.name.toLowerCase().includes(q)) ||
        (node.source && node.source.toLowerCase().includes(q)) ||
        (node.tags || []).some((x) => x.toLowerCase().includes(q))
      );
    };

    // & = alle Begriffe (AND), ; = irgendein Begriff (OR)
    let terms = null;
    let combinator = "every";
    if (term.includes("&")) {
      terms = term.split("&").map((s) => s.trim()).filter(Boolean);
      combinator = "every";
    } else if (term.includes(";")) {
      terms = term.split(";").map((s) => s.trim()).filter(Boolean);
      combinator = "some";
    } else if (term) {
      terms = [term];
    }

    const matchesTerm = (node) => !term || (terms && terms[combinator]((t) => matchOne(node, t)));
    const matchesFilters = (node) =>
      (activeFilters || []).every((f) => {
        const col = (f.column || "").toLowerCase();
        const val = (f.value || "").toLowerCase();
        if (col === "source") return node.source && node.source.toLowerCase().includes(val);
        if (col === "tag") return (node.tags || []).some((x) => x.toLowerCase().includes(val));
        return node.name && node.name.toLowerCase().includes(val);
      });

    const matching = new Set();
    const addAncestors = (node) => {
      let cur = node;
      let guard = 0;
      while (cur && cur.parentCode && tree[cur.parentCode] && guard < 20) {
        matching.add(cur.parentCode);
        cur = tree[cur.parentCode];
        guard += 1;
      }
    };

    Object.values(tree).forEach((node) => {
      if (matchesTerm(node) && matchesFilters(node)) {
        matching.add(node.code);
        addAncestors(node);
      }
    });
    return matching;
  }, [tree, searchTerm, activeFilters]);

  // === Sichtbarkeit ===
  const isRowVisible = useCallback(
    (node) => {
      if (showInactive) return true;
      if (node.active) return true;
      if (node.level === 1) return true;
      const t = totals[node.code];
      return !!(t && t.isAggregate);
    },
    [showInactive, totals]
  );

  // Hat die Position mindestens ein Kind, das aktuell gerendert wuerde? Gleiche
  // Bedingungen wie der Abstieg in collectVisible (Suchfilter + isRowVisible).
  // Steuert das Ausklapp-Dreieck: ohne sichtbare Kinder (keine Kinder, oder alle
  // ungenutzt und ausgeblendet) kein Dreieck; "Ungenutzte einblenden" bringt es
  // zurueck.
  const hasVisibleChildren = useCallback(
    (node) =>
      (node.children || []).some((childCode) => {
        const child = tree[childCode];
        if (!child) return false;
        if (filteredCodes && !filteredCodes.has(child.code)) return false;
        return isRowVisible(child);
      }),
    [tree, filteredCodes, isRowVisible]
  );

  const collectVisible = useCallback(
    (respectExpand) => {
      const out = [];
      const walk = (node, depth) => {
        if (!node) return;
        // Suchfilter: nur Treffer + deren Pfad zeigen
        if (filteredCodes && !filteredCodes.has(node.code)) return;
        if (!isRowVisible(node)) return;
        out.push({ node, depth });
        // Bei aktiver Suche Treffer immer zeigen (Zuklapp-Zustand ignorieren).
        const recurse = filteredCodes ? true : !respectExpand || expanded[node.code];
        if (recurse && node.children) {
          for (const childCode of node.children) {
            if (tree[childCode]) walk(tree[childCode], depth + 1);
          }
        }
      };
      Object.values(tree)
        .filter((n) => n.level === 1)
        .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10))
        .forEach((n) => walk(n, 0));
      return out;
    },
    [tree, expanded, isRowVisible, filteredCodes]
  );

  // === Inline-Edit (KV / Erwartung) ===
  const startEdit = (code, field, current) => {
    if (readOnly) return;
    setEditing({ code, field });
    setLocalValue(current != null ? String(current) : "");
  };
  const commitEdit = () => {
    if (!editing) return;
    editControlField(editing.code, editing.field, localValue);
    setEditing(null);
    setLocalValue("");
  };
  const cancelEdit = () => {
    setEditing(null);
    setLocalValue("");
  };
  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  // === Detail-Panels (KOST-013) ===
  const togglePanel = useCallback((code) => {
    setOpenPanels((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const detailCapableCodes = useMemo(
    () =>
      collectVisible(false)
        .filter(({ node }) => {
          const t = totals[node.code];
          return t && !t.isUnused; // Positionen und Aggregate
        })
        .map(({ node }) => node.code),
    [collectVisible, totals]
  );

  const allDetailsOpen =
    detailCapableCodes.length > 0 && detailCapableCodes.every((c) => openPanels.has(c));

  const toggleAllDetails = useCallback(() => {
    setOpenPanels((prev) => {
      const allOpen =
        detailCapableCodes.length > 0 && detailCapableCodes.every((c) => prev.has(c));
      return allOpen ? new Set() : new Set(detailCapableCodes);
    });
  }, [detailCapableCodes]);

  // === Vertrag/Zahlung CRUD-Handler ===
  const handleSaveContract = useCallback(
    async (code, payload) => {
      const saved = await saveContract(estimateId, { ...payload, code, phase: currentPhase });
      setContractsByCode((prev) => ({ ...prev, [code]: [...(prev[code] || []), saved] }));
    },
    [estimateId, currentPhase]
  );
  const handleUpdateContract = useCallback(
    async (code, id, payload) => {
      const updated = await updateContract(estimateId, id, { ...payload, code, phase: currentPhase });
      setContractsByCode((prev) => ({
        ...prev,
        [code]: (prev[code] || []).map((c) => (c.id === id ? updated : c)),
      }));
    },
    [estimateId, currentPhase]
  );
  const handleDeleteContract = useCallback(
    async (code, id) => {
      await deleteContract(estimateId, id);
      setContractsByCode((prev) => ({
        ...prev,
        [code]: (prev[code] || []).filter((c) => c.id !== id),
      }));
    },
    [estimateId]
  );

  const handleSavePayment = useCallback(
    async (code, payload) => {
      const saved = await savePayment(estimateId, { ...payload, code, phase: currentPhase });
      setPaymentsByCode((prev) => ({ ...prev, [code]: [...(prev[code] || []), saved] }));
    },
    [estimateId, currentPhase]
  );
  const handleUpdatePayment = useCallback(
    async (code, id, payload) => {
      const updated = await updatePayment(estimateId, id, { ...payload, code, phase: currentPhase });
      setPaymentsByCode((prev) => ({
        ...prev,
        [code]: (prev[code] || []).map((p) => (p.id === id ? updated : p)),
      }));
    },
    [estimateId, currentPhase]
  );
  const handleDeletePayment = useCallback(
    async (code, id) => {
      await deletePayment(estimateId, id);
      setPaymentsByCode((prev) => ({
        ...prev,
        [code]: (prev[code] || []).filter((p) => p.id !== id),
      }));
    },
    [estimateId]
  );

  // === Kontextmenü (KOST-012) ===
  const handleContextMenu = (e, node) => {
    if (readOnly) return;
    e.preventDefault();
    const t = totals[node.code];
    let kind;
    if (t && t.isUnused) kind = "unused";
    else if (t && t.isAggregate) kind = "aggregate";
    else kind = "position";
    setContextMenu({ x: e.clientX, y: e.clientY, code: node.code, nodeName: node.name || node.code, kind });
  };

  const openPanelWith = (code, what) => {
    // Detail-Panels rendern nur in der Kostenkontrolle-Ansicht — aus der
    // KV-Original-Ansicht heraus deshalb zuerst umschalten, sonst passiert
    // sichtbar nichts.
    if (view !== "control") changeView("control");
    setOpenPanels((prev) => new Set(prev).add(code));
    setPanelOpenWith((prev) => ({ ...prev, [code]: what }));
    setContextMenu(null);
  };

  // Zahlungsanweisung (US-KOST-022): Panel der Position oeffnen (dort landet die
  // neue Zahlung sichtbar) und das Modal starten. Mit `payment` wird eine
  // bestehende Zahlungsanweisung bearbeitet (US-KOST-023).
  const openZahlungsanweisung = (code, payment = null) => {
    if (view !== "control") changeView("control");
    setOpenPanels((prev) => new Set(prev).add(code));
    setContextMenu(null);
    setZaModal({ code, payment: payment || null });
  };

  // === Position löschen (KOST-017) ===
  // Trägt die Position (oder eine Unterposition) Verträge/Zahlungen, öffnet sich
  // ein Dialog: entweder werden sie einer anderen Position zugeordnet (Default:
  // direkte Überposition, sofern sie durch das Löschen zur Blattposition wird)
  // oder endgültig gelöscht. Ohne Wahl würden ihre Beträge nach dem Löschen
  // unsichtbar weiter in die Rollups einfliessen.
  const requestDeletePosition = (code) => {
    setContextMenu(null);
    const node = tree[code];
    if (!node) return;

    const subtreeCodes = [];
    const walk = (c) => {
      if (!tree[c]) return;
      subtreeCodes.push(c);
      for (const cc of tree[c].children || []) walk(cc);
    };
    walk(code);

    const affContracts = subtreeCodes.flatMap((c) => contractsByCode[c] || []);
    const affPayments = subtreeCodes.flatMap((c) => paymentsByCode[c] || []);

    const t = totals[code];
    const label = `${code}${node.name ? ` (${node.name})` : ""}`;

    if (affContracts.length === 0 && affPayments.length === 0) {
      const msg = t?.isAggregate
        ? `Position ${label} inkl. aller Unterpositionen löschen?`
        : `Position ${label} löschen?`;
      if (window.confirm(msg)) deletePosition(code);
      return;
    }

    // Zielpositionen fürs Umhängen: nur Positionen, die nach dem Löschen sicher
    // genutzte Blattpositionen sind — aktive Positionen ausserhalb des Astes,
    // plus die direkte Überposition, wenn sie ihren letzten aktiven Nachkommen
    // verliert (sie wird beim Löschen automatisch aktiviert). Auf Aggregat-
    // Zeilen wären umgehängte Verträge unsichtbar (Rollup summiert nur Kinder).
    const inSubtree = new Set(subtreeCodes);
    const targetOptions = Object.values(tree)
      .filter((n) => n.active && !inSubtree.has(n.code))
      .map((n) => n.code)
      .sort();
    const parentCode = node.parentCode;
    let parentQualifies = false;
    if (parentCode && tree[parentCode] && !tree[parentCode].active) {
      const hasOtherActiveDesc = (pc) =>
        (tree[pc]?.children || []).some((cc) => {
          if (inSubtree.has(cc)) return false;
          const child = tree[cc];
          return child && (child.active || hasOtherActiveDesc(cc));
        });
      parentQualifies = !hasOtherActiveDesc(parentCode);
      if (parentQualifies) targetOptions.unshift(parentCode);
    }

    setDeleteModal({
      code,
      label,
      isAggregate: !!t?.isAggregate,
      subtreeCodes,
      contracts: affContracts,
      payments: affPayments,
      mode: targetOptions.length > 0 ? "reassign" : "delete",
      targetCode: targetOptions[0] || "",
      targetOptions,
      parentCode: parentQualifies ? parentCode : null,
    });
  };

  const confirmDeleteWithDetails = async () => {
    const m = deleteModal;
    if (!m) return;
    if (m.mode === "reassign" && !m.targetCode) return;
    setDeleteBusy(true);
    try {
      const inSubtree = new Set(m.subtreeCodes);
      const stripSubtreeKeys = (prev) => {
        const next = {};
        for (const [k, v] of Object.entries(prev)) {
          if (!inSubtree.has(k)) next[k] = v;
        }
        return next;
      };

      if (m.mode === "reassign") {
        const movedContracts = [];
        for (const c of m.contracts) {
          movedContracts.push(await updateContract(estimateId, c.id, { ...c, code: m.targetCode, phase: currentPhase }));
        }
        const movedPayments = [];
        for (const p of m.payments) {
          movedPayments.push(await updatePayment(estimateId, p.id, { ...p, code: m.targetCode, phase: currentPhase }));
        }
        setContractsByCode((prev) => {
          const next = stripSubtreeKeys(prev);
          if (movedContracts.length) next[m.targetCode] = [...(next[m.targetCode] || []), ...movedContracts];
          return next;
        });
        setPaymentsByCode((prev) => {
          const next = stripSubtreeKeys(prev);
          if (movedPayments.length) next[m.targetCode] = [...(next[m.targetCode] || []), ...movedPayments];
          return next;
        });
      } else {
        for (const c of m.contracts) await deleteContract(estimateId, c.id);
        for (const p of m.payments) await deletePayment(estimateId, p.id);
        setContractsByCode(stripSubtreeKeys);
        setPaymentsByCode(stripSubtreeKeys);
      }

      deletePosition(m.code);
      setDeleteModal(null);
    } catch (err) {
      window.alert(
        `Verträge/Zahlungen konnten nicht ${m.mode === "reassign" ? "zugeordnet" : "gelöscht"} werden: ${err?.message || "Unbekannter Fehler"}. ` +
          "Die Position wurde nicht gelöscht."
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  // === KV-Basis übernehmen (KOST-006 / US-KOST-019) ===
  // Quelle wählbar aus allen Dossiers desselben Projekts. Seit US-KOST-019 sind
  // auch Kostenkontroll-Phasen ("41 ...") zulässige Quellen — "neue Kostenkontrolle
  // auf Basis einer alten": deren Werte sind bereits inkl. MwSt und werden 1:1
  // übernommen, wahlweise KV oder aktuelle Prognose (Re-Baseline). Nur die
  // aktuell geöffnete Kostenkontrolle selbst ist als Quelle ausgeschlossen.
  const kvSourcePhasesFor = (dossier) =>
    (dossier?.phases || []).filter(
      (p) => !(dossier?.id === currentEstimateId && p === currentPhase)
    );

  const defaultSourcePhase = (dossier) => {
    const usable = kvSourcePhasesFor(dossier);
    return usable.find((p) => p.trim().startsWith("32")) || usable[usable.length - 1] || "";
  };

  const openKvModal = (overwrite) => {
    const self = projectEstimates.find((e) => e.id === currentEstimateId) || projectEstimates[0] || null;
    const startId = self?.id || currentEstimateId || "";
    setKvSource({ estimateId: startId, phase: defaultSourcePhase(self), mode: "kv" });
    setKvModal({ overwrite });
  };

  const kvSourceDossier = projectEstimates.find((e) => e.id === kvSource.estimateId) || null;
  const kvSourcePhaseOptions = kvSourcePhasesFor(kvSourceDossier);
  const kvSourceIs41 = !!kvSource.phase && kvSource.phase.trim().startsWith("41");

  const onKvSourceDossierChange = (id) => {
    const dossier = projectEstimates.find((e) => e.id === id) || null;
    setKvSource({ estimateId: id, phase: defaultSourcePhase(dossier), mode: "kv" });
  };

  const confirmKvImport = async () => {
    if (!kvSource.phase) {
      window.alert("Bitte eine Quell-Phase wählen.");
      return;
    }
    setKvBaseBusy(true);
    try {
      await importKvBase({
        sourceEstimateId: kvSource.estimateId,
        sourcePhase: kvSource.phase,
        mode: kvSource.mode,
      });
      setKvModal(null);
    } catch (err) {
      window.alert(err?.message || "KV-Basis konnte nicht übernommen werden.");
    } finally {
      setKvBaseBusy(false);
    }
  };

  // === Excel-Export (KOST-015) ===
  const handleExport = () => {
    exportCostControlToExcel({
      view,
      nodes: collectVisible(false),
      totals,
      grand,
      percents,
      estimateName,
      linkedProject,
      currentPhase,
    });
  };

  // === Zell-Renderer ===
  const renderMoney = (value) => `CHF ${formatSwissCurrency(value)}`;

  const renderEditableMoney = (node, field, displayValue, editable, signed = false) => {
    const isEditing = editing?.code === node.code && editing?.field === field;
    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleEditKeyDown}
        />
      );
    }
    const content = renderMoney(displayValue);
    if (editable && !readOnly) {
      return (
        <span
          className="control-editable"
          title="Zum Bearbeiten klicken"
          onClick={() => startEdit(node.code, field, node[field] || 0)}
        >
          {content}
        </span>
      );
    }
    return content;
  };

  // Bezeichnung inline editieren (KOST-018): nur in der KV-Original-Ansicht,
  // damit die Kostenkontrolle-Ansicht eine reine Auswertungsansicht bleibt.
  const renderNameCellContent = (node, t, nameEditable) => {
    const isEditing = editing?.code === node.code && editing?.field === "name";
    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="text"
          className="control-name-input"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleEditKeyDown}
        />
      );
    }
    return (
      <>
        {nameEditable ? (
          <span
            className="control-editable"
            title="Zum Bearbeiten klicken"
            onClick={() => startEdit(node.code, "name", node.name || "")}
          >
            {node.name || <span className="control-name-placeholder">Bezeichnung eingeben</span>}
          </span>
        ) : (
          node.name || ""
        )}
        {t.zahlungOverridden && (
          <span className="zahlung-override-badge" title="Zahlungs-Rollup durch Überpositions-Zahlung übersteuert">
            ⚑
          </span>
        )}
      </>
    );
  };

  const controlColCount = 10; // expand, bkp, name, kv, vertrag, erwartung, prognose, zahlung, diff, aktion
  const kvColCount = 6; // expand, bkp, name, kv, %/H, aktion

  const renderRows = () => {
    const rows = [];
    const visible = collectVisible(true);

    for (const { node } of visible) {
      const t = totals[node.code] || {};
      const isAggregate = !!t.isAggregate;
      const isUnused = !!t.isUnused;
      const nodeHasChildren = hasVisibleChildren(node);
      const isExpanded = expanded[node.code];
      // KOST-018: auch ungenutzte Positionen sind direkt editierbar — der Edit
      // aktiviert sie (editControlField setzt active). Nur Aggregate bleiben
      // Rollup und damit read-only.
      const kvEditable = !isAggregate;
      const nameEditable = view === "kv" && !readOnly;

      const rowClass = [
        "control-row",
        `level-${node.level}`,
        isAggregate ? "aggregate-row" : "",
        isUnused ? "unused-row" : "",
        t.zahlungOverridden ? "zahlung-overridden" : "",
      ]
        .filter(Boolean)
        .join(" ");

      const diffClass =
        t.diff > 0.005 ? "diff-over" : t.diff < -0.005 ? "diff-under" : "diff-neutral";

      rows.push(
        <tr
          key={`row-${node.code}`}
          className={rowClass}
          onContextMenu={(e) => handleContextMenu(e, node)}
        >
          <td className="control-expand-cell">
            {nodeHasChildren && (
              <button className="expand-button" onClick={() => toggleExpand(node.code)}>
                {isExpanded ? "▼" : "▶"}
              </button>
            )}
          </td>
          <td className="control-code-cell">{node.code}</td>
          <td className="control-name-cell" title={node.name || ""}>
            {renderNameCellContent(node, t, nameEditable)}
          </td>

          {/* KV */}
          <td className="control-num kv-cell">
            {renderEditableMoney(node, "kvFixed", t.kv || 0, kvEditable)}
          </td>

          {view === "control" ? (
            <>
              <td className="control-num">{renderMoney(t.vertrag || 0)}</td>
              <td className="control-num erwartung-cell">
                {renderEditableMoney(node, "erwartung", t.erwartung || 0, !isAggregate, true)}
              </td>
              <td className="control-num">{renderMoney(t.prognose || 0)}</td>
              <td className="control-num">{renderMoney(t.zahlung || 0)}</td>
              <td className={`control-num diff-cell ${diffClass}`}>{renderMoney(t.diff || 0)}</td>
              <td className="control-action-cell">
                {isUnused ? (
                  !readOnly && (
                    <button className="link-btn" onClick={() => activatePosition(node.code)}>
                      verwenden
                    </button>
                  )
                ) : (
                  <button className="link-btn" onClick={() => togglePanel(node.code)}>
                    {openPanels.has(node.code) ? "schliessen" : "Details"}
                  </button>
                )}
              </td>
            </>
          ) : (
            <>
              <td className="control-num percent-cell">
                {percents[node.code] != null ? `${percents[node.code].toFixed(1)} %` : "—"}
              </td>
              <td className="control-action-cell">
                {isUnused && !readOnly && (
                  <button
                    className="link-btn"
                    onClick={() => activatePosition(node.code)}
                    title="Position aus der BKP-Vorlage in den KV aufnehmen"
                  >
                    verwenden
                  </button>
                )}
              </td>
            </>
          )}
        </tr>
      );

      // Kommentarzeile: direkt an die BKP-Position angehängt, nur Text in der
      // Bezeichnungs-Spalte (kursiv), alle übrigen Zellen bewusst leer.
      const isEditingComment = editing?.code === node.code && editing?.field === "comment";
      if (node.comment || isEditingComment) {
        const restCols = (view === "control" ? controlColCount : kvColCount) - 3;
        rows.push(
          <tr key={`comment-${node.code}`} className="control-comment-row">
            <td className="control-expand-cell"></td>
            <td className="control-code-cell"></td>
            <td
              className="control-name-cell comment-cell"
              onClick={() => {
                if (readOnly || isEditingComment) return;
                startEdit(node.code, "comment", node.comment || "");
              }}
            >
              {isEditingComment ? (
                <input
                  ref={inputRef}
                  type="text"
                  className="control-name-input"
                  value={localValue}
                  placeholder="Kommentar..."
                  onChange={(e) => setLocalValue(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={handleEditKeyDown}
                />
              ) : (
                <span className="comment-text">{node.comment}</span>
              )}
            </td>
            <td colSpan={restCols}></td>
          </tr>
        );
      }

      // Detail-Panel (nur Kostenkontrolle-Ansicht). Zwei leere Vorlaufzellen
      // (Expand + BKP), damit das Panel exakt unter der Bezeichnung beginnt.
      if (view === "control" && openPanels.has(node.code) && !isUnused) {
        rows.push(
          <tr key={`detail-${node.code}`} className="control-detail-tr">
            <td className="control-expand-cell control-detail-lead"></td>
            <td className="control-code-cell control-detail-lead"></td>
            <td colSpan={controlColCount - 2}>
              <CostControlDetailPanel
                code={node.code}
                nodeName={node.name}
                contracts={contractsByCode[node.code] || []}
                payments={paymentsByCode[node.code] || []}
                readOnly={readOnly}
                openWith={panelOpenWith[node.code] || null}
                onSaveContract={handleSaveContract}
                onUpdateContract={(id, payload) => handleUpdateContract(node.code, id, payload)}
                onDeleteContract={(id) => handleDeleteContract(node.code, id)}
                onSavePayment={handleSavePayment}
                onUpdatePayment={(id, payload) => handleUpdatePayment(node.code, id, payload)}
                onDeletePayment={(id) => handleDeletePayment(node.code, id)}
                onZahlungsanweisung={openZahlungsanweisung}
                partners={partners}
                onPartnersReload={reloadPartners}
                projectCode={linkedProject?.code || ""}
              />
            </td>
          </tr>
        );
      }
    }
    return rows;
  };

  const showKvBaseHint = !kvBaseImported && !treeHasData && !readOnly;

  return (
    <div className="cost-control-editor">
      {/* Toolbar */}
      <div className="control-toolbar">
        <div className="control-toolbar-left">
          <div className="control-view-switch" role="tablist">
            <button
              className={`segment ${view === "kv" ? "active" : ""}`}
              onClick={() => changeView("kv")}
            >
              KV-Original
            </button>
            <button
              className={`segment ${view === "control" ? "active" : ""}`}
              onClick={() => changeView("control")}
            >
              Kostenkontrolle
            </button>
          </div>

          <label className="control-inline-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Ungenutzte Positionen einblenden
          </label>

          {view === "control" && (
            <label className="control-inline-toggle">
              <input type="checkbox" checked={allDetailsOpen} onChange={toggleAllDetails} />
              Alle Details einblenden
            </label>
          )}
        </div>

        <div className="control-toolbar-right">
          <SaveStatusIndicator
            isDirty={isDirty}
            isSaving={isSaving}
            lastSavedAt={lastSavedAt}
            lastSaveKind={lastSaveKind}
          />

          {!readOnly && !kvBaseImported && (
            <button
              className="btn btn-primary btn-small"
              onClick={() => openKvModal(false)}
              disabled={kvBaseBusy || isSaving}
              title="Kostenschätzung aus einer früheren Phase als KV-Basis übernehmen (inkl. MwSt)"
            >
              KV-Basis übernehmen
            </button>
          )}
          {!readOnly && kvBaseImported && (
            <button
              className="btn btn-secondary btn-small"
              onClick={() => openKvModal(true)}
              disabled={kvBaseBusy || isSaving}
              title="KV-Basis erneut übernehmen (überschreibt bestehende Werte)"
            >
              KV-Basis neu übernehmen
            </button>
          )}

          {canManageMembers && onManageMembers && (
            <button
              className="btn btn-secondary btn-small"
              onClick={onManageMembers}
              title="Team-Mitglieder dem Projekt zuordnen (gleicher Zugriff wie Pendenzen, Termine, Controlling)"
            >
              Personen zuordnen
            </button>
          )}

          <button className="btn btn-secondary btn-small" onClick={handleExport} title="Aktuelle Ansicht als Excel exportieren">
            Excel Export
          </button>

          {!readOnly && (
            <button className="btn btn-primary btn-small" onClick={() => saveBatch("manual")} disabled={isSaving || !isDirty}>
              {isSaving ? "Speichern..." : "Speichern"}
            </button>
          )}

          {toggleFullscreen && (
            <button className="fullscreen-btn" onClick={toggleFullscreen} title={isFullscreen ? "Vollbild beenden" : "Vollbild"}>
              {isFullscreen ? "✖" : "⛶"}
            </button>
          )}
        </div>
      </div>

      {/* Summenkarten */}
      <div className="control-summary-cards">
        <div className="control-summary-card">
          <span className="card-label">KV Total</span>
          <span className="card-value">{renderMoney(grand.kv)}</span>
        </div>
        {view === "control" && (
          <>
            <div className="control-summary-card">
              <span className="card-label">Prognose Total</span>
              <span className="card-value">{renderMoney(grand.prognose)}</span>
            </div>
            <div className="control-summary-card">
              <span className="card-label">Diff Total</span>
              <span
                className={`card-value ${grand.diff > 0.005 ? "diff-over" : grand.diff < -0.005 ? "diff-under" : ""}`}
              >
                {renderMoney(grand.diff)}
              </span>
            </div>
          </>
        )}
      </div>

      {loadError && <div className="error-message">{loadError}</div>}
      {showKvBaseHint && (
        <div className="empty-hint">
          Noch keine KV-Basis vorhanden. Übernehmen Sie die Kostenschätzung aus Phase 32 als KV-Basis.
        </div>
      )}

      {/* Tabelle */}
      <div className="control-table-container">
        <table className="control-table">
          <thead>
            <tr>
              <th className="control-expand-col"></th>
              <th className="control-code-col">BKP</th>
              <th className="control-name-col">Bezeichnung</th>
              <th className="control-num">KV</th>
              {view === "control" ? (
                <>
                  <th className="control-num">Vertrag</th>
                  <th className="control-num">Erwartung</th>
                  <th className="control-num">Prognose</th>
                  <th className="control-num">Zahlung</th>
                  <th className="control-num">Diff</th>
                  <th className="control-action-col"></th>
                </>
              ) : (
                <>
                  <th className="control-num">%/H</th>
                  <th className="control-action-col"></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {dataLoading ? (
              <tr>
                <td colSpan={view === "control" ? controlColCount : kvColCount} style={{ textAlign: "center", padding: "1.5rem", opacity: 0.6 }}>
                  Kostenkontrolle wird geladen...
                </td>
              </tr>
            ) : (
              renderRows()
            )}
          </tbody>
        </table>
      </div>

      {/* Kontextmenü */}
      {contextMenu && (
        <div className="context-menu" style={{ top: contextMenu.y, left: contextMenu.x, position: "fixed" }} onClick={(e) => e.stopPropagation()}>
          <div className="context-menu-header">{contextMenu.nodeName}</div>
          {tree[contextMenu.code]?.comment ? (
            <>
              <button
                className="context-menu-item"
                onClick={() => {
                  startEdit(contextMenu.code, "comment", tree[contextMenu.code]?.comment || "");
                  setContextMenu(null);
                }}
              >
                Kommentar bearbeiten
              </button>
              <button
                className="context-menu-item danger"
                onClick={() => {
                  editControlField(contextMenu.code, "comment", "");
                  setContextMenu(null);
                }}
              >
                Kommentar löschen
              </button>
            </>
          ) : (
            <button
              className="context-menu-item"
              onClick={() => {
                startEdit(contextMenu.code, "comment", "");
                setContextMenu(null);
              }}
            >
              Kommentar hinzufügen
            </button>
          )}
          {contextMenu.kind === "aggregate" && (
            <>
              <button
                className="context-menu-item"
                onClick={() => {
                  addChild(contextMenu.code);
                  setContextMenu(null);
                }}
              >
                Unterposition hinzufügen
              </button>
              <button
                className="context-menu-item danger"
                onClick={() => requestDeletePosition(contextMenu.code)}
              >
                Position inkl. Unterpositionen löschen
              </button>
            </>
          )}
          {contextMenu.kind === "position" && (
            <>
              <button className="context-menu-item" onClick={() => openPanelWith(contextMenu.code, "contract")}>
                Vertrag / Nachtrag hinzufügen
              </button>
              <button className="context-menu-item" onClick={() => openPanelWith(contextMenu.code, "payment")}>
                Zahlung hinzufügen
              </button>
              <button className="context-menu-item" onClick={() => openZahlungsanweisung(contextMenu.code)}>
                Zahlungsanweisung erstellen
              </button>
              <button className="context-menu-item" onClick={() => openPanelWith(contextMenu.code, "unternehmer")}>
                Unternehmen zuordnen
              </button>
              <button
                className="context-menu-item danger"
                onClick={() => requestDeletePosition(contextMenu.code)}
              >
                Position löschen
              </button>
            </>
          )}
          {contextMenu.kind === "unused" && (
            <button
              className="context-menu-item"
              onClick={() => {
                activatePosition(contextMenu.code);
                setContextMenu(null);
              }}
            >
              Position verwenden
            </button>
          )}
        </div>
      )}

      {/* Zahlungsanweisung als PDF + Zahlung eintragen (US-KOST-022) */}
      {zaModal && (
        <ZahlungsanweisungModal
          code={zaModal.code}
          nodeName={tree[zaModal.code]?.name || ""}
          contracts={contractsByCode[zaModal.code] || []}
          payments={paymentsByCode[zaModal.code] || []}
          allPayments={allPayments}
          phase={currentPhase}
          existingPayment={zaModal.payment}
          objektgliederung={objektgliederung}
          estimateId={estimateId}
          estimateClient={estimateClient}
          linkedProject={linkedProject}
          currentUserName={currentUserName}
          partners={partners}
          onPartnersReload={reloadPartners}
          onSavePayment={handleSavePayment}
          onUpdatePayment={handleUpdatePayment}
          onUpdateContract={handleUpdateContract}
          onClose={() => setZaModal(null)}
        />
      )}

      {/* KV-Basis-Quellwahl (KOST-006): Dossier + Phase desselben Projekts */}
      {kvModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setKvModal(null); }}>
          <div className="modal-content estimate-modal">
            <div className="modal-header">
              <h3>KV-Basis übernehmen</h3>
              <button className="modal-close" onClick={() => setKvModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="override-hint">
                {kvSourceIs41
                  ? "Die Quelle ist eine Kostenkontrolle: Ihre Werte sind bereits inkl. MwSt und werden 1:1 übernommen — wahlweise deren KV oder deren aktuelle Prognose als neuer KV."
                  : "Übernimmt pro Position den aggregierten Total der gewählten Quelle × 1.081 (MwSt) als fixen KV-Wert."}
              </p>
              {kvModal.overwrite && (
                <div className="error-message">
                  Achtung: Die bestehende KV-Basis wird überschrieben.
                </div>
              )}
              <div className="form-group">
                <label>Quell-Kostenschätzung (gleiches Projekt)</label>
                <select
                  value={kvSource.estimateId}
                  onChange={(e) => onKvSourceDossierChange(e.target.value)}
                >
                  {projectEstimates.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                      {e.id === currentEstimateId ? " (dieses Dossier)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Quell-Phase</label>
                <select
                  value={kvSource.phase}
                  onChange={(e) => setKvSource((prev) => ({ ...prev, phase: e.target.value }))}
                >
                  {kvSourcePhaseOptions.length === 0 && <option value="">Keine geeignete Phase</option>}
                  {kvSourcePhaseOptions.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              {kvSourceIs41 && (
                <div className="form-group phase-mode-choice">
                  <label>
                    <input
                      type="radio"
                      name="kv-import-mode"
                      checked={kvSource.mode !== "prognose"}
                      onChange={() => setKvSource((prev) => ({ ...prev, mode: "kv" }))}
                    />
                    KV der Quelle übernehmen (1:1)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="kv-import-mode"
                      checked={kvSource.mode === "prognose"}
                      onChange={() => setKvSource((prev) => ({ ...prev, mode: "prognose" }))}
                    />
                    Prognose der Quelle als neuer KV übernehmen (Re-Baseline)
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setKvModal(null)} disabled={kvBaseBusy}>
                Abbrechen
              </button>
              <button className="btn btn-primary" onClick={confirmKvImport} disabled={kvBaseBusy || !kvSource.phase}>
                {kvBaseBusy ? "Übernehme..." : "Übernehmen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Löschen einer Position mit Verträgen/Zahlungen (KOST-017) */}
      {deleteModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !deleteBusy) setDeleteModal(null); }}>
          <div className="modal-content estimate-modal">
            <div className="modal-header">
              <h3>Position löschen</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)} disabled={deleteBusy}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="override-hint">
                Position {deleteModal.label} wird {deleteModal.isAggregate ? "inkl. aller Unterpositionen " : ""}gelöscht.
                Darauf {deleteModal.contracts.length + deleteModal.payments.length === 1 ? "ist" : "sind"}{" "}
                {deleteModal.contracts.length > 0 && `${deleteModal.contracts.length} Vertrag/Nachtrag${deleteModal.contracts.length > 1 ? "e" : ""}`}
                {deleteModal.contracts.length > 0 && deleteModal.payments.length > 0 && " und "}
                {deleteModal.payments.length > 0 && `${deleteModal.payments.length} Zahlung${deleteModal.payments.length > 1 ? "en" : ""}`}
                {" "}erfasst. Was soll damit geschehen?
              </p>
              <div className="form-group delete-details-choice">
                <label className={`control-inline-toggle ${deleteModal.targetOptions.length === 0 ? "disabled" : ""}`}>
                  <input
                    type="radio"
                    name="delete-details-mode"
                    checked={deleteModal.mode === "reassign"}
                    disabled={deleteBusy || deleteModal.targetOptions.length === 0}
                    onChange={() => setDeleteModal((m) => ({ ...m, mode: "reassign" }))}
                  />
                  Einer anderen Position zuordnen
                </label>
                {deleteModal.mode === "reassign" && (
                  <select
                    value={deleteModal.targetCode}
                    disabled={deleteBusy}
                    onChange={(e) => setDeleteModal((m) => ({ ...m, targetCode: e.target.value }))}
                  >
                    {deleteModal.targetOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                        {tree[c]?.name ? ` — ${tree[c].name}` : ""}
                        {c === deleteModal.parentCode ? " (direkte Überposition)" : ""}
                      </option>
                    ))}
                  </select>
                )}
                <label className="control-inline-toggle">
                  <input
                    type="radio"
                    name="delete-details-mode"
                    checked={deleteModal.mode === "delete"}
                    disabled={deleteBusy}
                    onChange={() => setDeleteModal((m) => ({ ...m, mode: "delete" }))}
                  />
                  Endgültig löschen
                </label>
                {deleteModal.mode === "delete" && (
                  <div className="error-message">
                    Achtung: Die Verträge/Zahlungen werden unwiderruflich gelöscht.
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteModal(null)} disabled={deleteBusy}>
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={confirmDeleteWithDetails}
                disabled={deleteBusy || (deleteModal.mode === "reassign" && !deleteModal.targetCode)}
              >
                {deleteBusy ? "Lösche..." : "Position löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostControlEditor;
